import { useEffect, useMemo, useState } from 'react';

import { useSmartLinkContext } from '@atlaskit/link-provider/use-smart-link-context';
import type { SmartLinkResponse } from '@atlaskit/linking-types/smart-link';

import { getSmartLinkRovoMetadata, type SmartLinkRovoMetadata } from './smartlink-metadata';

export const useResolvedSmartLinkForRovo = ({
	resolveIfMissing = false,
	smartlinkUrl,
}: {
	resolveIfMissing?: boolean;
	smartlinkUrl: string;
}): {
	details?: SmartLinkResponse;
	isLoading: boolean;
	isResolvedWithAccess: boolean;
	metadata?: SmartLinkRovoMetadata;
} => {
	const { store, connections } = useSmartLinkContext();
	const [isResolving, setIsResolving] = useState(false);
	const smartLinkState = store?.getState()[smartlinkUrl];
	const { status, details } = smartLinkState || {};

	// For plain hyperlinks, the URL is not yet in the SmartLink store (only card
	// components trigger resolution automatically). We trigger resolution on mount
	// so we can check access permissions before showing AI buttons.
	useEffect(() => {
		if (!resolveIfMissing || smartLinkState || !smartlinkUrl) {
			return;
		}

		setIsResolving(true);
		connections.client
			.fetchData(smartlinkUrl)
			.then((response) => {
				store?.dispatch({
					type: 'resolved',
					url: smartlinkUrl,
					payload: response,
				});
				setIsResolving(false);
			})
			.catch(() => {
				// If resolution fails, buttons won't render — correct behavior
				// for inaccessible URLs.
			});
	}, [connections.client, resolveIfMissing, smartLinkState, smartlinkUrl, store]);

	const isResolvedWithAccess = status === 'resolved' && details?.meta?.access === 'granted';
	const metadata = useMemo(
		() =>
			details
				? getSmartLinkRovoMetadata({
						details,
						smartlinkUrl,
					})
				: undefined,
		[details, smartlinkUrl],
	);

	return {
		details,
		isLoading: isResolving,
		isResolvedWithAccess,
		metadata,
	};
};
